import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Img, Stack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {FormattedMessage} from 'react-intl'
import {AddToCartIcon} from '../icons'
import PriceCard from '../price-card'
import {findImageGroupBy} from '../../utils/image-groups-utils'
import {remCalc} from '../../theme/_utils'
import {getProductUrl} from '../../hooks/use-product-data'
import {getPriceData} from '../../utils/product-utils'

const ProductUpgrade = ({recommendations = []}) => {
    const styles = useMultiStyleConfig('ProductUpgrade')
    const navigate = useNavigation()
    const {locale} = useMultiSite()
    const localeId = locale?.alias
    const {currency: activeCurrency} = useCurrency()

    const recommendationIds = useMemo(() => {
        const ids = recommendations.map((rec) => rec?.recommendedItemId).filter(Boolean)
        return [...new Set(ids)]
    }, [recommendations])

    const {data: productsData, isLoading} = useProducts(
        {
            parameters: {
                ids: recommendationIds.join(','),
                expand: ['availability', 'images', 'prices', 'promotions'],
            },
            headers: {
                'x-debug': 'product-upgrade',
            },
        },
        {
            enabled: recommendationIds.length > 0,
        },
    )

    const upgradeProduct = useMemo(() => {
        const products = productsData?.data || []
        return (
            products.find((item) => item?.inventory?.orderable !== false) ||
            products.find(Boolean) ||
            null
        )
    }, [productsData?.data])

    const priceData = useMemo(() => {
        return upgradeProduct ? getPriceData(upgradeProduct) : null
    }, [upgradeProduct])

    if (!recommendationIds.length || isLoading || !upgradeProduct || !priceData) {
        return null
    }

    const description = (
        upgradeProduct?.shortDescription ||
        upgradeProduct?.c_subscriptionShortDescription ||
        ''
    )
        .replace(/<[^>]*>/g, '')
        .trim()

    const image =
        upgradeProduct?.image?.link ||
        upgradeProduct?.image?.disBaseLink ||
        findImageGroupBy(upgradeProduct?.imageGroups, {
            viewType: 'large',
            selectedVariationAttributes: upgradeProduct?.variationValues,
        })?.images?.[0]?.link ||
        ''

    const productUrl = upgradeProduct ? getProductUrl(upgradeProduct) : null

    const handleNavigateToUpgradePdp = () => {
        if (!productUrl || !localeId) return
        navigate(`/${localeId}${productUrl}`)
    }

    return (
        <Stack {...styles.container} data-testid="product-upgrade">
            <Stack {...styles.titleUpgrade}>
                <Text as="h2" {...styles.title}>
                    <FormattedMessage
                        id="product_view.upgrade.title"
                        defaultMessage="Upgrade title"
                    />
                </Text>
                <Text {...styles.subtitle}>
                    <FormattedMessage
                        id="product_view.upgrade.subtitle"
                        defaultMessage="Get the milk experience with Rivelia LatteCrema only."
                    />
                </Text>
            </Stack>
            <Stack {...styles.card}>
                <Flex {...styles.topRow}>
                    <Box {...styles.imagePlaceholder} aria-hidden>
                        {image ? (
                            <Img
                                src={image}
                                alt={upgradeProduct?.name || upgradeProduct?.productName || ''}
                                width="100%"
                                height="100%"
                                objectFit="contain"
                                borderRadius="medium-1"
                                style={{
                                    background:
                                        'linear-gradient(0deg, rgba(0, 0, 0, 0.10) 0%, rgba(0, 0, 0, 0.10) 100%), url(<path-to-image>) lightgray 50% / contain no-repeat',
                                }}
                            />
                        ) : null}
                    </Box>

                    <Stack {...styles.content}>
                        <Text {...styles.productName}>
                            {upgradeProduct?.c_variantIdentifier ||
                                upgradeProduct?.name ||
                                upgradeProduct?.productName}
                        </Text>
                        <Text {...styles.productDescription}>{description}</Text>
                    </Stack>
                </Flex>

                <Flex {...styles.bottomRow}>
                    <Box {...styles.priceRow}>
                        <PriceCard
                            priceData={priceData}
                            product={upgradeProduct}
                            activeCurrency={upgradeProduct?.currency || activeCurrency}
                            variant="productUpgrade"
                            showOmnibusPrice={true}
                            placeInfoPopoverInOmnibus={false}
                            showDisplayPriceInfoWithOmnibus={true}
                            weeeFeeVariant="productView"
                            extractWeeeFromPrice={true}
                            showWeeeFee={false}
                            showVatLabel={false}
                            productDetailCurrentPriceTextStyle="body-small"
                            productDetailListPriceTextStyle="body-small"
                        />
                    </Box>

                    <Button
                        {...styles.addButton}
                        aria-label="Go to upgrade product PDP"
                        size="small"
                        border="small"
                        borderColor="border.neutral.highlight"
                        leftIcon={
                            <AddToCartIcon width={remCalc(16)} height={remCalc(16)} fill="none" />
                        }
                        variant={'icon-button-outline'}
                        onClick={handleNavigateToUpgradePdp}
                        isDisabled={!productUrl || !localeId}
                        paddingX={remCalc(10)}
                        paddingY={remCalc(10)}
                    />
                </Flex>
            </Stack>
        </Stack>
    )
}

ProductUpgrade.propTypes = {
    recommendations: PropTypes.array,
}

export default ProductUpgrade
